<template>
  <div>
    <woot-modal :show.sync="localShowStatusDialog" :on-close="closeStatusModal">
      <div class="dialog-body">
        <h3>{{ $t('OPPORTUNITY_DETAILS.STATUS_MODAL.TITLE') }}</h3>
        <p
          v-dompurify-html="statusMainMessageHighlighted"
          class="status-main-message"
        />
        <p class="status-current-stage">
          <strong>{{ $t('OPPORTUNITY_DETAILS.STATUS_MODAL.CURRENT_STAGE') }}:</strong>
          <strong class="status-stage-value">
            {{
              (opportunity &&
                opportunity.pipeline_stage &&
                opportunity.pipeline_stage.name) ||
                '-'
            }}
          </strong>
        </p>
        <p class="status-helper-text">
          {{ $t('OPPORTUNITY_DETAILS.STATUS_MODAL.HELPER_TEXT') }}
        </p>

        <div class="status-form-grid">
          <label v-if="statusAction === 'lost'" class="field-block field-panel">
            <span>{{
              $t('OPPORTUNITY_DETAILS.STATUS_MODAL.LOSS_REASON')
            }}</span>
            <select v-model="statusForm.lossReasonId">
              <option :value="null">-</option>
              <option
                v-for="reason in activeLossReasons"
                :key="reason.id"
                :value="reason.id"
              >
                {{ reason.name }}
              </option>
            </select>
          </label>

          <opportunity-stage-select
            v-model="statusForm.pipelineStageId"
            :stages="activeTimelineStages"
            :current-stage-id="opportunity && opportunity.pipeline_stage_id"
            :label="$t('OPPORTUNITY_DETAILS.STATUS_MODAL.PIPELINE_STAGE')"
            panel
          />

          <label class="field-block note-panel">
            <span>{{ $t('OPPORTUNITY_DETAILS.STATUS_MODAL.NOTE') }}</span>
            <textarea
              v-model="statusForm.note"
              rows="4"
              :placeholder="$t('OPPORTUNITY_DETAILS.NOTE_PLACEHOLDER')"
            />
          </label>
        </div>

        <div class="dialog-actions">
          <woot-button
            color-scheme="secondary"
            variant="smooth"
            @click="closeStatusModal"
          >
            {{ $t('OPPORTUNITY_DETAILS.BUTTONS.CANCEL') }}
          </woot-button>
          <woot-button
            :is-loading="isSubmittingStatus || isCheckingActivities"
            class="dialog-save-button"
            @click="submitStatusChange"
          >
            {{ $t('OPPORTUNITY_DETAILS.STATUS_MODAL.SAVE') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <woot-confirm-modal
      ref="pendingActivitiesConfirmation"
      :title="$t('OPPORTUNITY_DETAILS.STATUS_MODAL.PENDING_ACTIVITIES.TITLE')"
      :description="
        $t('OPPORTUNITY_DETAILS.STATUS_MODAL.PENDING_ACTIVITIES.DESCRIPTION')
      "
      :confirm-label="
        $t('OPPORTUNITY_DETAILS.STATUS_MODAL.PENDING_ACTIVITIES.CONFIRM')
      "
      :cancel-label="
        $t('OPPORTUNITY_DETAILS.STATUS_MODAL.PENDING_ACTIVITIES.BACK')
      "
    />

    <woot-modal :show.sync="localShowEditDialog" :on-close="closeEditModal">
      <div class="dialog-body">
        <h3>{{ $t('OPPORTUNITY_DETAILS.EDIT_MODAL.TITLE') }}</h3>

        <label class="field-block">
          <span>{{ $t('OPPORTUNITY_DETAILS.EDIT_MODAL.NAME') }}</span>
          <input v-model="editForm.name" type="text" />
        </label>

        <label class="field-block">
          <span>{{ $t('OPPORTUNITY_DETAILS.EDIT_MODAL.DESCRIPTION') }}</span>
          <input v-model="editForm.description" type="text" />
        </label>

        <opportunity-stage-select
          v-model="editForm.pipelineStageId"
          :stages="activeTimelineStages"
          :current-stage-id="opportunity && opportunity.pipeline_stage_id"
          :label="$t('OPPORTUNITY_DETAILS.EDIT_MODAL.STAGE')"
        />

        <label class="field-block">
          <span>{{ $t('OPPORTUNITY_DETAILS.EDIT_MODAL.VALUE') }}</span>
          <decimal-input v-model="editForm.value" />
        </label>

        <div class="dialog-actions">
          <woot-button
            color-scheme="secondary"
            variant="smooth"
            @click="closeEditModal"
          >
            {{ $t('OPPORTUNITY_DETAILS.BUTTONS.CANCEL') }}
          </woot-button>
          <woot-button :is-loading="isSubmittingEdit" @click="submitEdit">
            {{ $t('OPPORTUNITY_DETAILS.EDIT_MODAL.SAVE') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import DecimalInput from 'dashboard/components/widgets/forms/DecimalInput.vue';
import OpportunityStageSelect from './OpportunityStageSelect.vue';

export default {
  name: 'OpportunityModals',
  components: {
    DecimalInput,
    OpportunityStageSelect,
  },
  mixins: [alertMixin],
  props: {
    opportunity: {
      type: Object,
      default: null,
    },
    showStatusDialog: {
      type: Boolean,
      default: false,
    },
    showEditDialog: {
      type: Boolean,
      default: false,
    },
    statusAction: {
      type: String,
      default: 'won',
    },
    activeTimelineStages: {
      type: Array,
      default: () => [],
    },
    activeLossReasons: {
      type: Array,
      default: () => [],
    },
    isSubmittingStatus: {
      type: Boolean,
      default: false,
    },
    isSubmittingEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCheckingActivities: false,
      statusDialogToken: 0,
      statusForm: {
        lossReasonId: null,
        pipelineStageId: null,
        note: '',
      },
      editForm: {
        name: '',
        description: '',
        pipelineStageId: null,
        value: 0,
      },
    };
  },
  computed: {
    opportunityId() {
      return this.opportunity && this.opportunity.id;
    },
    localShowStatusDialog: {
      get() {
        return this.showStatusDialog;
      },
      set(val) {
        if (!val) {
          this.closeStatusModal();
        }
      },
    },
    localShowEditDialog: {
      get() {
        return this.showEditDialog;
      },
      set(val) {
        if (!val) {
          this.closeEditModal();
        }
      },
    },
    statusModalMessageKey() {
      if (this.statusAction === 'lost') {
        return 'OPPORTUNITY_DETAILS.STATUS_MODAL.LOST_MESSAGE';
      }

      if (this.statusAction === 'canceled') {
        return 'OPPORTUNITY_DETAILS.STATUS_MODAL.CANCELED_MESSAGE';
      }

      return 'OPPORTUNITY_DETAILS.STATUS_MODAL.WON_MESSAGE';
    },
    statusMainMessageHighlighted() {
      const message = this.$t(this.statusModalMessageKey);
      const keywords = [
        'Aberto',
        'Ganho',
        'Perdido',
        'Cancelado',
        'Open',
        'Won',
        'Lost',
        'Canceled',
      ];

      return keywords.reduce((text, word) => {
        const regex = new RegExp(`\\b${word}\\b`, 'g');
        return text.replace(regex, `<strong>${word}</strong>`);
      }, String(message));
    },
  },
  watch: {
    showStatusDialog(val) {
      this.invalidateStatusOperation();
      if (val) {
        this.statusForm = {
          lossReasonId: null,
          pipelineStageId:
            (this.opportunity && this.opportunity.pipeline_stage_id) || null,
          note: '',
        };
      }
    },
    opportunityId(newId, oldId) {
      if (newId !== oldId) this.invalidateStatusOperation();
    },
    showEditDialog(val) {
      if (val) {
        this.editForm = {
          name: (this.opportunity && this.opportunity.name) || '',
          description: (this.opportunity && this.opportunity.description) || '',
          pipelineStageId:
            (this.opportunity && this.opportunity.pipeline_stage_id) || null,
          value: Number((this.opportunity && this.opportunity.value) || 0),
        };
      }
    },
  },
  methods: {
    closeStatusModal() {
      this.$emit('close-status');
    },
    closeEditModal() {
      this.$emit('close-edit');
    },
    invalidateStatusOperation() {
      this.statusDialogToken += 1;
      this.isCheckingActivities = false;
      const confirmation = this.$refs.pendingActivitiesConfirmation;
      if (confirmation && confirmation.show) confirmation.cancel();
    },
    isCurrentStatusOperation(token, opportunityId) {
      return (
        this.showStatusDialog &&
        token === this.statusDialogToken &&
        opportunityId === this.opportunityId
      );
    },
    async hasPendingActivities(opportunityId) {
      try {
        const activities = await this.$store.dispatch(
          'opportunities/getActivities',
          opportunityId
        );
        return (activities || []).some(activity => activity.status === 'open');
      } catch (error) {
        return true;
      }
    },
    async submitStatusChange() {
      if (this.statusAction === 'lost' && !this.statusForm.lossReasonId) {
        this.showAlert(
          this.$t('OPPORTUNITY_DETAILS.ERRORS.LOSS_REASON_REQUIRED')
        );
        return;
      }
      if (!this.opportunityId || this.isCheckingActivities) return;

      const { opportunityId, statusDialogToken: token } = this;
      const statusForm = { ...this.statusForm };
      this.isCheckingActivities = true;
      const mustConfirm = await this.hasPendingActivities(opportunityId);
      if (!this.isCurrentStatusOperation(token, opportunityId)) return;
      this.isCheckingActivities = false;

      if (mustConfirm) {
        const confirmed = await this.$refs.pendingActivitiesConfirmation.showConfirmation();
        if (
          !confirmed ||
          !this.isCurrentStatusOperation(token, opportunityId)
        ) {
          return;
        }
      }

      this.$emit('submit-status', statusForm);
    },
    submitEdit() {
      this.$emit('submit-edit', { ...this.editForm });
    },
  },
};
</script>

<style lang="scss" scoped>
.dialog-body {
  max-width: 68rem;
  padding: var(--space-large);
}

.dialog-body h3 {
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--space-small);
}

.status-main-message {
  margin-bottom: var(--space-small);
}

.status-current-stage {
  margin-bottom: var(--space-smaller);
}

.status-stage-value {
  font-weight: var(--font-weight-semibold);
}

.status-helper-text {
  color: var(--s-700);
  margin-bottom: var(--space-medium);
}

.status-form-grid {
  display: grid;
  gap: var(--space-small);
}

.field-panel {
  background: var(--s-50);
  border-radius: var(--border-radius-small);
  padding: var(--space-small);
}

.note-panel {
  background: var(--s-50);
  border-radius: var(--border-radius-small);
  padding: var(--space-normal);
}

.note-panel-actions {
  display: none;
}

.field-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-micro);
  margin-top: var(--space-small);
}

.field-block input,
.field-block select,
.field-block textarea {
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-small);
  padding: var(--space-smaller);
}

.field-block select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath fill='%23111827' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
  background-position: right 0.9rem center;
  background-repeat: no-repeat;
  background-size: 0.75rem;
  padding-right: 2.2rem;
}

.dialog-actions {
  display: flex;
  justify-content: space-between;
  margin-top: var(--space-medium);
}

.dialog-save-button {
  min-width: 8rem;
}
</style>
